import React, { Suspense, lazy } from 'react';
import { Route, Switch, Redirect } from 'react-router-dom';
import * as paths from './paths';

import { Loading } from '@atoms';
import { DesktopLayoutRoute, MobileLayoutRoute } from './Layout';

const HomePage = lazy(() => import('@src/pages/Home'));
const LoginPage = lazy(() => import('@src/pages/Login'));
const SignupPage = lazy(() => import('@src/pages/SignUp'));
const ReferInvitePage = lazy(() => import('@src/components/pages/ReferInvitePage'));
// Account
const WelcomePage = lazy(() => import('@src/components/pages/WelcomePage'));
const AccountPage = lazy(() => import('@src/components/pages/AccountPage'));
const AccountDeletePage = lazy(() => import('@src/components/pages/AccountDeletePage'));
const AccountDeleteConfirmPage = lazy(() => import('@src/components/pages/AccountDeleteConfirmPage'));
const OrderListPage = lazy(() => import('@src/components/pages/OrderListPage'));
const OrderDetailPage = lazy(() => import('@src/components/pages/OrderDetailPage'));
const MyReviewPage = lazy(() => import('@src/components/pages/MyReviewPage'));
const MyPointPage = lazy(() => import('@src/components/pages/MyPointPage'));
const MyVoucherPage = lazy(() => import('@components/pages/MyVoucherPage'));
const WishListPage = lazy(() => import('@src/pages/WishList'));
const SharePage = lazy(() => import('@src/components/pages/SharePage'));
const ChangePasswordPage = lazy(() => import('@src/components/pages/ChangePasswordPage'));
const ContactPage = lazy(() => import('@src/components/pages/ContactPage'));
const WriteReviewPage = lazy(() => import('@src/components/pages/WriteReviewPage'));
const PendingReviewPage = lazy(() => import('@src/components/pages/PendingReviewPage'));
// Cart & Checkout
const CartPage = lazy(() => import('@src/pages/Cart'));
const CheckoutPage = lazy(() => import('@src/components/pages/CheckoutPage'));
const CheckoutSuccessPage = lazy(() => import('@src/components/pages/CheckoutSuccessPage'));
// Password & Email Authentication
const PasswordResetPage = lazy(() => import('@src/pages/PasswordReset'));
const PasswordResetProcessPage = lazy(() => import('@src/pages/PasswordResetProcess'));
const EmailVerifyRequestPage = lazy(() => import('@src/pages/EmailVerifyRequest'));
const EmailVerifyResultPage = lazy(() => import('@src/pages/EmailVerifyResult'));
// Product List
const BrandPage = lazy(() => import('@src/pages/ProductList/Brand'));
const CategoryPage = lazy(() => import('@src/pages/ProductList/Category'));
const CollectionPage = lazy(() => import('@src/pages/ProductList/Collection'));
const DisplayPage = lazy(() => import('@src/pages/ProductList/Display'));
const SearchResultPage = lazy(() => import('@src/pages/SearchResult'));
const TagPage = lazy(() => import('@src/pages/ProductList/Tag'));
const VoucherPage = lazy(() => import('@src/pages/ProductList/Voucher'));
const SearchPage = lazy(() => import('@src/pages/Search'));
const ExhibitionPage = lazy(() => import('@src/pages/Exhibition'));
const OffersPage = lazy(() => import('@src/pages/Offers'));
const DesktopBrandListPage = lazy(() => import('@src/pages/BrandList/DesktopBrandList'));
const MobileShopTabPage = lazy(() => import('@src/pages/ShopTab/MobileShopTabPage'));
const MobileMgsPage = lazy(() => import('@src/pages/Mgs/MobileMgsPage'));
const MobileOTPVerificationPage = lazy(() => import('@src/pages/OTPVerification/MobileOTPVerificationPage'));
// ProductDetail
const ProductDetailPage = lazy(() => import('@src/pages/ProductDetail'));

// App Download
const CommunitySharePage = lazy(() => import('src/pages/CommunityShare'));
const MaccaronExperienceSharePage = lazy(() => import('src/pages/ExperienceShare'));
const MstiTestPage = lazy(() => import('src/pages/MstiTest'));
import MstiResultPage from '@src/components/pages/MstiResultPage/MstiResultPage';
const RankingPage = lazy(() => import('src/pages/Ranking'));

// Check
const FaqPage = lazy(() => import('@src/components/pages/FaqPage'));
const FaqTopicPage = lazy(() => import('@src/components/pages/FaqTopicPage'));
const FaqSearchPage = lazy(() => import('@src/components/pages/FaqSearchPage'));
const StaticPage = lazy(() => import('@src/components/pages/StaticPage'));
const FlashSalePage = lazy(() => import('@src/components/pages/FlashSalePage'));
const DailyMarketingPage = lazy(() => import('@src/components/pages/DailyMarketingPage'));
const BrandStoryPage = lazy(() => import('@src/components/pages/BrandStoryPage'));
const TrackingPage = lazy(() => import('@src/components/TrackingPage'));

const Routes = ({ isMobile }) => (
  <Suspense fallback={<Loading />}>
    <Switch>
      <Route exact path="/">
        <Redirect to={paths.baseUrl} />
      </Route>
      {isMobile ? (
        <>
          <MobileLayoutRoute exact path={paths.baseUrl} component={HomePage} />
          <MobileLayoutRoute
            exact
            path={paths.accountLoginUrl}
            component={LoginPage}
            showHeader={false}
            showFooter={false}
            showBottomNav={false}
          />
          <MobileLayoutRoute
            exact
            path={paths.accountSignupUrl}
            component={SignupPage}
            showHeader={false}
            showFooter={false}
            showBottomNav={false}
          />
          <MobileLayoutRoute
            exact
            path={paths.signupWithCodeUrl}
            component={SignupPage}
            showHeader={false}
            showFooter={false}
            showBottomNav={false}
          />
          <MobileLayoutRoute exact path={paths.referInviteUrl} component={ReferInvitePage} />
          {/* Account */}
          <MobileLayoutRoute exact path={paths.welcomeUrl} component={WelcomePage} />
          <MobileLayoutRoute exact path={paths.accountUrl} component={AccountPage} />
          <MobileLayoutRoute exact path={paths.accountDeleteUrl} component={AccountDeletePage} />
          <MobileLayoutRoute exact path={paths.accountDeleteConfirmUrl} component={AccountDeleteConfirmPage} />
          <MobileLayoutRoute exact path={paths.orderListUrl} component={OrderListPage} />
          <MobileLayoutRoute exact path={paths.activeOrderListUrl} component={OrderListPage} />
          <MobileLayoutRoute exact path={paths.orderDetailUrl} component={OrderDetailPage} />
          <MobileLayoutRoute exact path={paths.reviewListUrl} component={MyReviewPage} />
          <MobileLayoutRoute exact path={paths.pointListUrl} component={MyPointPage} />
          <MobileLayoutRoute exact path={paths.voucherListUrl} component={MyVoucherPage} />
          <MobileLayoutRoute
            exact
            path={paths.wishListUrl}
            component={WishListPage}
            showHeader={false}
            showFooter={false}
            showBottomNav={false}
          />
          <MobileLayoutRoute exact path={paths.shareUrl} component={SharePage} />
          <MobileLayoutRoute exact path={paths.changePasswordUrl} component={ChangePasswordPage} />
          <MobileLayoutRoute exact path={paths.contactUrl} component={ContactPage} />
          <MobileLayoutRoute exact path={paths.writeReviewUrl} component={WriteReviewPage} />
          <MobileLayoutRoute exact path={paths.pendingReviewListUrl} component={PendingReviewPage} />
          {/*Cart & Checkout*/}
          <MobileLayoutRoute
            exact
            path={paths.cartUrl}
            component={CartPage}
            showHeader={false}
            showFooter={false}
            showBottomNav={false}
          />
          <MobileLayoutRoute
            exact
            path={paths.checkoutUrl}
            component={CheckoutPage}
            showHeader={false}
            showFooter={false}
            showBottomNav={false}
          />
          <MobileLayoutRoute exact path={paths.orderCheckoutSuccessUrl} component={CheckoutSuccessPage} />
          {/* Password & Email Authentication*/}
          <MobileLayoutRoute
            exact
            path={paths.passwordResetUrl}
            component={PasswordResetPage}
            showFooter={false}
            showBottomNav={false}
          />
          <MobileLayoutRoute
            exact
            path={paths.passwordResetProcessUrl}
            component={PasswordResetProcessPage}
            showFooter={false}
            showBottomNav={false}
          />
          <MobileLayoutRoute
            exact
            path={paths.emailVerifyRequestUrl}
            component={EmailVerifyRequestPage}
            showHeader={false}
            showFooter={false}
            showBottomNav={false}
          />
          <MobileLayoutRoute
            exact
            path={paths.emailVerifyResultUrl}
            component={EmailVerifyResultPage}
            showHeader={false}
            showFooter={false}
            showBottomNav={false}
          />
          {/* Product List */}
          <MobileLayoutRoute
            exact
            path={paths.brandUrl}
            component={BrandPage}
            showHeader={false}
            showFooter={false}
            showBottomNav={false}
          />
          <MobileLayoutRoute
            exact
            path={paths.categoryUrl}
            component={CategoryPage}
            showHeader={false}
            showFooter={false}
            showBottomNav={false}
          />
          <MobileLayoutRoute
            exact
            path={paths.collectionUrl}
            component={CollectionPage}
            showHeader={false}
            showFooter={false}
            showBottomNav={false}
          />
          <MobileLayoutRoute
            exact
            path={paths.displayUrl}
            component={DisplayPage}
            showHeader={false}
            showFooter={false}
            showBottomNav={false}
          />
          <MobileLayoutRoute
            exact
            path={paths.searchResultUrl}
            component={SearchResultPage}
            showHeader={false}
            showFooter={false}
            showBottomNav={false}
          />
          <MobileLayoutRoute
            exact
            path={paths.productListByTagUrl}
            component={TagPage}
            showHeader={false}
            showFooter={false}
            showBottomNav={false}
          />
          <MobileLayoutRoute
            exact
            path={paths.productListByVoucher}
            component={VoucherPage}
            showHeader={false}
            showFooter={false}
            showBottomNav={false}
          />
          <MobileLayoutRoute
            exact
            path={paths.searchUrl}
            component={SearchPage}
            showHeader={false}
            showFooter={false}
            showBottomNav={false}
          />
          <MobileLayoutRoute
            exact
            path={paths.exhibitionsUrl}
            component={ExhibitionPage}
            showHeader={false}
            showFooter={false}
            showBottomNav={false}
          />
          <MobileLayoutRoute exact path={paths.offersUrl} component={OffersPage} />
          <MobileLayoutRoute
            exact
            path={paths.mobileShopTabUrl}
            component={MobileShopTabPage}
            showHeader={false}
            showFooter={false}
          />
          <MobileLayoutRoute exact path={paths.mobileMainMgsTabUrl} component={MobileMgsPage} />
          <MobileLayoutRoute
            exact
            path={paths.mobileOTPVerificationUrl}
            component={MobileOTPVerificationPage}
            showHeader={false}
            showFooter={false}
            showBottomNav={false}
          />
          {/* ProductDetail */}
          <MobileLayoutRoute
            exact
            path={paths.productDetailUrl}
            component={ProductDetailPage}
            showHeader={false}
            showFooter={false}
            showBottomNav={false}
          />
          <MobileLayoutRoute
            exact
            path={paths.productDetailUrl2}
            component={ProductDetailPage}
            showHeader={false}
            showFooter={false}
            showBottomNav={false}
          />
          {/* App Download */}
          <MobileLayoutRoute
            exact
            path={paths.communityShareUrl}
            component={CommunitySharePage}
            showHeader={true}
            isHeaderMargin={false}
            showFooter={false}
            showBottomNav={true}
          />
          <MobileLayoutRoute
            exact
            path={paths.maccaronExperienceShareUrl}
            component={MaccaronExperienceSharePage}
            showHeader={true}
            isHeaderMargin={false}
            showFooter={false}
            showBottomNav={true}
          />
          <MobileLayoutRoute
            exact
            path={paths.mstiTestUrl}
            component={MstiTestPage}
            showHeader={true}
            isHeaderMargin={false}
            showFooter={false}
            showBottomNav={true}
          />
          <MobileLayoutRoute exact path={paths.mstiResultUrlWithSlug} component={MstiResultPage} />
          <MobileLayoutRoute
            exact
            path={paths.rankingUrl}
            component={RankingPage}
            showHeader={true}
            isHeaderMargin={false}
            showFooter={false}
            showBottomNav={true}
          />
          {/* Check */}
          <MobileLayoutRoute exact path={paths.faqUrl} component={FaqPage} />
          <MobileLayoutRoute exact path={paths.faqTopicUrl} component={FaqTopicPage} />
          <MobileLayoutRoute exact path={paths.faqSearchUrl} component={FaqSearchPage} />
          <MobileLayoutRoute exact path={paths.staticPageUrl} component={StaticPage} />
          <MobileLayoutRoute exact path={paths.flashSaleUrl} component={FlashSalePage} />
          {/*<MobileLayoutRoute exact path={paths.dailyMarketingUrl} component={DailyMarketingPage} />*/}
          <MobileLayoutRoute exact path={paths.brandStoryUrl} component={BrandStoryPage} />
          <MobileLayoutRoute exact path={paths.trackingUrl} component={TrackingPage} />
          {/* <MobileLayoutRoute component={NotFound} /> */}
        </>
      ) : (
        <>
          <DesktopLayoutRoute exact path={paths.baseUrl} component={HomePage} isHeaderMargin={false} />
          <DesktopLayoutRoute exact path={paths.accountLoginUrl} component={LoginPage} isHeaderMargin={false} />
          <DesktopLayoutRoute exact path={paths.accountSignupUrl} component={SignupPage} isHeaderMargin={false} />
          <DesktopLayoutRoute exact path={paths.signupWithCodeUrl} component={SignupPage} isHeaderMargin={false} />
          <DesktopLayoutRoute exact path={paths.referInviteUrl} component={ReferInvitePage} />
          {/* Account */}
          <DesktopLayoutRoute exact path={paths.welcomeUrl} component={WelcomePage} />
          <DesktopLayoutRoute exact path={paths.accountUrl} component={AccountPage} />
          <DesktopLayoutRoute exact path={paths.accountDeleteUrl} component={AccountDeletePage} />
          <DesktopLayoutRoute exact path={paths.accountDeleteConfirmUrl} component={AccountDeleteConfirmPage} />
          <DesktopLayoutRoute exact path={paths.orderListUrl} component={OrderListPage} />
          <DesktopLayoutRoute exact path={paths.activeOrderListUrl} component={OrderListPage} />
          <DesktopLayoutRoute exact path={paths.orderDetailUrl} component={OrderDetailPage} />
          <DesktopLayoutRoute exact path={paths.reviewListUrl} component={MyReviewPage} />
          <DesktopLayoutRoute exact path={paths.pointListUrl} component={MyPointPage} />
          <DesktopLayoutRoute exact path={paths.voucherListUrl} component={MyVoucherPage} />
          <DesktopLayoutRoute
            exact
            path={paths.wishListUrl}
            component={WishListPage}
            isHeaderMargin={false}
            showFooter={false}
          />
          <DesktopLayoutRoute exact path={paths.shareUrl} component={SharePage} />
          <DesktopLayoutRoute exact path={paths.changePasswordUrl} component={ChangePasswordPage} />
          <DesktopLayoutRoute exact path={paths.contactUrl} component={ContactPage} />
          <DesktopLayoutRoute exact path={paths.writeReviewUrl} component={WriteReviewPage} />
          <DesktopLayoutRoute exact path={paths.pendingReviewListUrl} component={PendingReviewPage} />
          {/*Cart & Checkout*/}
          <DesktopLayoutRoute
            exact
            path={paths.cartUrl}
            component={CartPage}
            showFooter={false}
            isHeaderMargin={false}
          />
          <DesktopLayoutRoute
            exact
            path={paths.checkoutUrl}
            component={CheckoutPage}
            showHeader={false}
            showFooter={false}
          />
          <DesktopLayoutRoute exact path={paths.orderCheckoutSuccessUrl} component={CheckoutSuccessPage} />
          {/* Password & Email Authentication*/}
          <DesktopLayoutRoute
            exact
            path={paths.passwordResetUrl}
            component={PasswordResetPage}
            isHeaderMargin={false}
          />
          <DesktopLayoutRoute
            exact
            path={paths.passwordResetProcessUrl}
            component={PasswordResetProcessPage}
            isHeaderMargin={false}
          />
          <DesktopLayoutRoute
            exact
            path={paths.emailVerifyRequestUrl}
            component={EmailVerifyRequestPage}
            isHeaderMargin={false}
          />
          <DesktopLayoutRoute
            exact
            path={paths.emailVerifyResultUrl}
            component={EmailVerifyResultPage}
            isHeaderMargin={false}
          />
          {/* Product List */}
          <DesktopLayoutRoute exact path={paths.brandUrl} component={BrandPage} isHeaderMargin={false} />
          <DesktopLayoutRoute exact path={paths.categoryUrl} component={CategoryPage} isHeaderMargin={false} />
          <DesktopLayoutRoute exact path={paths.collectionUrl} component={CollectionPage} isHeaderMargin={false} />
          {/*<DesktopLayoutRoute exact path={paths.displayUrl} component={DisplayPage} isHeaderMargin={false} />*/}
          <DesktopLayoutRoute exact path={paths.searchResultUrl} component={SearchResultPage} isHeaderMargin={false} />
          <DesktopLayoutRoute exact path={paths.productListByTagUrl} component={TagPage} isHeaderMargin={false} />
          <DesktopLayoutRoute exact path={paths.productListByVoucher} component={VoucherPage} isHeaderMargin={false} />
          <DesktopLayoutRoute exact path={paths.searchUrl} component={SearchPage} />
          <DesktopLayoutRoute exact path={paths.exhibitionsUrl} component={ExhibitionPage} isHeaderMargin={false} />
          <DesktopLayoutRoute exact path={paths.offersUrl} component={OffersPage} />
          <DesktopLayoutRoute exact path={paths.brandListUrl} component={DesktopBrandListPage} />
          {/* ProductDetail */}
          <DesktopLayoutRoute
            exact
            path={paths.productDetailUrl}
            component={ProductDetailPage}
            isHeaderMargin={false}
          />
          <DesktopLayoutRoute
            exact
            path={paths.productDetailUrl2}
            component={ProductDetailPage}
            isHeaderMargin={false}
          />
          {/* App Download */}
          <DesktopLayoutRoute
            exact
            path={paths.communityShareUrl}
            component={CommunitySharePage}
            isHeaderMargin={false}
            showFooter={false}
          />
          <DesktopLayoutRoute
            exact
            path={paths.maccaronExperienceShareUrl}
            component={MaccaronExperienceSharePage}
            isHeaderMargin={false}
            showFooter={false}
          />
          <DesktopLayoutRoute
            exact
            path={paths.mstiTestUrl}
            component={MstiTestPage}
            isHeaderMargin={false}
            showFooter={false}
          />
          <DesktopLayoutRoute exact path={paths.mstiResultUrlWithSlug} component={MstiResultPage} />
          <DesktopLayoutRoute
            exact
            path={paths.rankingUrl}
            component={RankingPage}
            isHeaderMargin={false}
            showFooter={false}
          />
          {/* Check */}
          <DesktopLayoutRoute exact path={paths.faqUrl} component={FaqPage} />
          <DesktopLayoutRoute exact path={paths.faqTopicUrl} component={FaqTopicPage} />
          <DesktopLayoutRoute exact path={paths.faqSearchUrl} component={FaqSearchPage} />
          <DesktopLayoutRoute exact path={paths.staticPageUrl} component={StaticPage} />
          <DesktopLayoutRoute exact path={paths.flashSaleUrl} component={FlashSalePage} />
          {/*<DesktopLayoutRoute exact path={paths.dailyMarketingUrl} component={DailyMarketingPage} />*/}
          <DesktopLayoutRoute exact path={paths.brandStoryUrl} component={BrandStoryPage} />
          <DesktopLayoutRoute exact path={paths.trackingUrl} component={TrackingPage} />
          {/* <DesktopLayoutRoute component={NotFound} /> */}
        </>
      )}
    </Switch>
  </Suspense>
);

export default Routes;
